import { useEffect, useState } from 'react';
import {
  api,
  type ControlSummary,
  type DemoFixture,
  type DriftReport,
  type Framework,
  type ProductSummary,
  type ScanResult,
} from './api.ts';
import { Drift } from './Drift.tsx';
import { Findings } from './Findings.tsx';
import './tryit.css';

/**
 * The pipeline, exercised without a tenant.
 *
 * There is an ordering problem in this product: nothing can be collected until
 * an Entra application is registered and a customer's Global Administrator has
 * consented, but almost nothing worth reviewing depends on collection. Whether
 * a finding reads well, whether a licence gap is distinguishable from a
 * failure, whether drift is legible in a monthly review — all of that can be
 * judged now, and until this screen existed there was no way to judge any of it
 * short of running a CLI command.
 *
 * The rules engine, the catalogue and the renderer are the real ones. Only the
 * collector is replaced, by a fixture that has already been through the
 * normaliser's schema. Nothing here writes to the database, and no client
 * tenant is involved in anything on this page — which is why the framing is a
 * notice at the top rather than a caveat at the bottom.
 */

const PAIRING: Record<string, string> = {
  // The drifted fixture only means anything against the tenant it drifted from.
  'compliant-tenant-drifted': 'compliant-tenant',
};

const errorText = (cause: unknown): string =>
  cause instanceof Error ? cause.message : String(cause);

export const TryIt = () => {
  const [fixtures, setFixtures] = useState<DemoFixture[]>([]);
  const [controls, setControls] = useState<ControlSummary[]>([]);
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [drift, setDrift] = useState<DriftReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .demoFixtures()
      .then((response) => {
        setFixtures(response.fixtures);
        setSelected(response.fixtures[0]?.name ?? null);
      })
      .catch((cause: unknown) => setError(errorText(cause)));

    // A finding carries a control id, not a title — the catalogue owns the
    // wording so that one edit changes it everywhere. Without this every
    // finding renders as a bare "AC-002", which is exactly the unreadable
    // output the catalogue exists to avoid.
    api
      .listControls()
      .then((response) => {
        setControls(response.controls);
        setProducts(response.products);
      })
      .catch((cause: unknown) => setError(errorText(cause)));

    // Only for the scope filter, and the demo can live without it: a failure
    // here leaves the product filter working and the framework levels absent,
    // which is better than an error over a demo that otherwise ran.
    api
      .listFrameworks()
      .then((response) => setFrameworks(response.frameworks))
      .catch(() => setFrameworks([]));
  }, []);

  const run = async (fixture: string) => {
    setBusy(true);
    setError(null);
    setDrift(null);
    try {
      const scan = await api.demoScan(fixture);
      setResult(scan.result);

      // Where a fixture is a later observation of another, show what moved as
      // well as where it ended up. That comparison is the monthly service
      // review, and it is the hardest part of the product to judge in the
      // abstract.
      const baseline = PAIRING[fixture];
      if (baseline) setDrift((await api.demoDrift(baseline, fixture)).drift);
    } catch (cause) {
      setError(errorText(cause));
    } finally {
      setBusy(false);
    }
  };

  const openReport = (path: string, options?: { print?: boolean }) => {
    api.openRendered(path, options).catch((cause: unknown) => setError(errorText(cause)));
  };

  const baseline = selected ? PAIRING[selected] : undefined;

  return (
    <div className="try-screen">
      <p className="lead">
        Run the assessment against a sample tenant. This is the real rules engine, the real
        catalogue and the real report — only the collector is replaced, so you can see exactly what
        a customer would get before any tenant is connected.
      </p>

      {/* The framing, not a footnote. Somebody evaluating the product needs to
          know before they press anything that no client is involved and that
          nothing they do here is recorded against one. */}
      <div className="callout try-notice">
        <span className="callout-title">A sandbox — nothing here is saved</span>
        No client tenant is read, no consent is used and nothing is written to the database. The
        sample tenants are fixtures bundled with the product, so this screen works before an Entra
        application has been registered.
      </div>

      {error && <div className="error">{error}</div>}

      <section className="card try-run" aria-labelledby="try-run-heading">
        <div className="card-head">
          <h2 id="try-run-heading">Sample tenant</h2>
        </div>

        <div className="card-body">
          <div className="field try-pick">
            <label htmlFor="fixture">Choose a sample tenant</label>
            <select
              id="fixture"
              value={selected ?? ''}
              onChange={(event) => setSelected(event.target.value)}
            >
              {fixtures.map((fixture) => (
                <option key={fixture.name} value={fixture.name}>
                  {fixture.label}
                </option>
              ))}
            </select>
          </div>

          <div className="toolbar">
            <button
              type="button"
              className="btn btn-primary"
              aria-busy={busy || undefined}
              disabled={busy || !selected}
              onClick={() => selected && void run(selected)}
            >
              {busy ? 'Running…' : 'Run assessment'}
            </button>

            {selected && (
              <button
                type="button"
                className="btn btn-quiet"
                onClick={() => openReport(api.demoReportPath(selected))}
              >
                Open the full report
              </button>
            )}

            {selected && (
              <button
                type="button"
                className="btn btn-quiet"
                onClick={() => openReport(api.demoReportPath(selected), { print: true })}
              >
                Save as PDF
              </button>
            )}

            {selected && baseline && (
              <button
                type="button"
                className="btn btn-quiet"
                onClick={() => openReport(api.demoDriftReportPath(baseline, selected))}
              >
                Open the report with drift
              </button>
            )}

            {selected && baseline && (
              <button
                type="button"
                className="btn btn-quiet"
                onClick={() =>
                  openReport(api.demoDriftReportPath(baseline, selected), { print: true })
                }
              >
                Save the drift report as PDF
              </button>
            )}
          </div>

          <p className="note try-help">
            The report is the document a customer receives: one self-contained HTML file with no
            network dependency. Saving as PDF opens it and then your browser&rsquo;s print
            dialogue — choose &ldquo;Save as PDF&rdquo; as the destination to produce the file.
          </p>
        </div>
      </section>

      {result && (
        <>
          <section className="card try-outcome" aria-labelledby="try-outcome-heading">
            <div className="card-head">
              <h2 id="try-outcome-heading">{result.tenantDisplayName}</h2>
              <p className="note">
                <span className="num">{result.findings.length}</span> controls assessed
              </p>
            </div>

            {/*
              What was assessed, and what assessed it. The verdict tally is not
              repeated here: <Findings> below renders the four counts as its own
              filter tiles, and two tallies a hundred pixels apart — one of them
              clickable and one not — is a worse screen than one tally that
              works. The provenance line is the part this card owns, because a
              reader has to be able to say which snapshot and which catalogue
              version produced what they are looking at.
            */}
            <div className="card-body">
              <p className="note try-provenance">
                Licences held: {result.licences.join(', ') || 'none reported'}. Snapshot{' '}
                <span className="mono">{result.snapshotHash}</span>, catalogue{' '}
                <span className="mono">{result.catalogueVersion}</span>, engine{' '}
                <span className="mono">{result.engineVersion}</span>. Assessed against the same
                rules engine a connected tenant is assessed with.
              </p>
            </div>
          </section>

          {drift && (
            <section className="try-section" aria-labelledby="try-drift-heading">
              <h2 id="try-drift-heading">What changed since the previous scan</h2>
              <Drift drift={drift} />
            </section>
          )}

          <section className="try-section" aria-labelledby="try-findings-heading">
            <h2 id="try-findings-heading">Findings</h2>
            <Findings
              findings={result.findings}
              controls={controls}
              products={products}
              frameworks={frameworks}
            />
          </section>
        </>
      )}
    </div>
  );
};
